import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Button, Divider, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IMetadataOther, IVessel } from "interfaces";
import React, { useState } from "react";
import { formatMoney } from "components/metadata/formatMoney";
import {
  VISIBLE_OTHER_SERVICE_CATALOG,
  OTHER_PRODUCT_COLORS,
  OtherServiceDef,
  WIFI_DONGLE_VISIBLE,
  readOtherField,
  fmtAnnualFee,
  fmtCurrency,
  fmtDiscountOrAmount,
  fmtWifiDongle,
} from "components/metadata/otherServiceCatalog";

const { Text } = Typography;

interface ServiceRow {
  key: string;
  product: string;
  service: string;
  status: string;
  subscriptionType: string;
  currency: string;
  annualFlatFee: string;
  discount: string;
  wifiDongle?: string;
}

const buildServiceRows = (record: IMetadataOther): ServiceRow[] =>
  VISIBLE_OTHER_SERVICE_CATALOG.map((svc) => ({
    key: svc.prefix,
    product: svc.product,
    service: svc.service,
    status: readOtherField<string>(record, `${svc.prefix}Status`) ?? "inactive",
    subscriptionType:
      readOtherField<string>(record, `${svc.prefix}SubscriptionType`) ?? "—",
    currency: fmtCurrency(svc, record),
    annualFlatFee: fmtAnnualFee(svc, record),
    discount: fmtDiscountOrAmount(svc, record),
    wifiDongle:
      svc.hasWifiDongle && WIFI_DONGLE_VISIBLE ? fmtWifiDongle(record) : undefined,
  }));

const hasAnyActive = (record: IMetadataOther): boolean =>
  VISIBLE_OTHER_SERVICE_CATALOG.some(
    (svc) => readOtherField<string>(record, `${svc.prefix}Status`) === "active",
  );

const getActiveServices = (record: IMetadataOther): OtherServiceDef[] =>
  VISIBLE_OTHER_SERVICE_CATALOG.filter(
    (svc) => readOtherField<string>(record, `${svc.prefix}Status`) === "active",
  );

const SERVICE_FILTER_OPTIONS = VISIBLE_OTHER_SERVICE_CATALOG.map((svc) => ({
  label: `${svc.product} - ${svc.service}`,
  value: `subscription.metadataOthers.${svc.prefix}Status`,
}));

const exportAttrsFor = (svc: OtherServiceDef): Array<{ suffix: string; label: string }> => [
  { suffix: "Status", label: "Status" },
  { suffix: "SubscriptionType", label: "Subscription Type" },
  { suffix: "Currency", label: "Currency" },
  { suffix: "AnnualFlatFeePerVessel", label: "Annual Flat Fee" },
  svc.usesPayPerUseAmount
    ? { suffix: "PayPerUseAmount", label: "Pay Per Use Amount" }
    : { suffix: "DiscountPercentage", label: "Discount %" },
];

const buildExportColumns = (): string[] => {
  const cols = [
    "UKHO Licence ID",
    "IMO Number",
    "Vessel Name",
    "AVCS Flat Fee Amount",
    "AVCS Flat Fee Currency",
  ];
  VISIBLE_OTHER_SERVICE_CATALOG.forEach((svc) => {
    exportAttrsFor(svc).forEach((attr) => {
      cols.push(`${svc.product} — ${svc.service} — ${attr.label}`);
    });
    if (svc.hasWifiDongle && WIFI_DONGLE_VISIBLE) {
      cols.push(`${svc.product} — ${svc.service} — Wi-Fi Dongle One-Time Cost`);
      cols.push(`${svc.product} — ${svc.service} — Wi-Fi Dongle Currency`);
    }
  });
  return cols;
};

const buildExportRow = (item: IMetadataOther): Record<string, unknown> => {
  const row: Record<string, unknown> = {
    "UKHO Licence ID": item?.subscription?.licence?.ukhoLicenceId || "",
    "IMO Number": item?.subscription?.licence?.vessel?.imoNumber || "",
    "Vessel Name": item?.subscription?.licence?.vessel?.name || "",
    "AVCS Flat Fee Amount":
      item?.avcsFlatFeeAmount == null
        ? ""
        : formatMoney(null, item.avcsFlatFeeAmount),
    "AVCS Flat Fee Currency": item?.avcsFlatFeeCurrency ?? "",
  };
  VISIBLE_OTHER_SERVICE_CATALOG.forEach((svc) => {
    exportAttrsFor(svc).forEach((attr) => {
      const header = `${svc.product} — ${svc.service} — ${attr.label}`;
      const raw = readOtherField<unknown>(item, `${svc.prefix}${attr.suffix}`);
      const isMoney =
        attr.suffix === "AnnualFlatFeePerVessel" ||
        attr.suffix === "PayPerUseAmount";
      row[header] = raw == null ? "" : isMoney ? formatMoney(null, raw) : raw;
    });
    if (svc.hasWifiDongle && WIFI_DONGLE_VISIBLE) {
      const dongleCost = readOtherField<number>(
        item,
        `${svc.prefix}WifiDongleOneTimeCost`,
      );
      row[`${svc.product} — ${svc.service} — Wi-Fi Dongle One-Time Cost`] =
        dongleCost == null ? "" : formatMoney(null, dongleCost);
      row[`${svc.product} — ${svc.service} — Wi-Fi Dongle Currency`] =
        readOtherField<string>(item, `${svc.prefix}WifiDongleCurrency`) ?? "";
    }
  });
  return row;
};

export const MetadataOtherList: React.FC<IResourceComponentsProps> = () => {
  const [selectedService, setSelectedService] = useState<string>();
  const {
    tableProps,
    tableQuery: { isLoading },
    setFilters,
    filters,
  } = useTable<IMetadataOther>({
    sorters: {
      initial: [{ field: "id", order: "desc" }],
    },
  });

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<IMetadataOther>({
      mapData: buildExportRow,
      unparseConfig: {
        header: true,
        columns: buildExportColumns(),
      },
    });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: { mode: "server" },
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: { mode: "server" },
  });

  const expandedRowRender = (record: IMetadataOther) => {
    const rows = buildServiceRows(record);
    const nestedColumns: ColumnsType<ServiceRow> = [
      { title: "Product", dataIndex: "product", key: "product", width: 220 },
      { title: "Service", dataIndex: "service", key: "service", width: 240 },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (value: string) => (
          <Tag color={value === "active" ? "green" : "orange"}>{value}</Tag>
        ),
      },
      {
        title: "Subscription Type",
        dataIndex: "subscriptionType",
        key: "subscriptionType",
        width: 200,
      },
      {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
        width: 110,
      },
      {
        title: "Annual Flat Fee",
        dataIndex: "annualFlatFee",
        key: "annualFlatFee",
        width: 160,
      },
      {
        title: "Discount / Per-Use Amount",
        dataIndex: "discount",
        key: "discount",
        width: 180,
      },
    ];
    return (
      <Table
        columns={nestedColumns}
        dataSource={rows}
        pagination={false}
        size="small"
        rowKey="key"
        expandable={{
          expandedRowRender: (row) =>
            row.wifiDongle ? (
              <Text type="secondary">{row.wifiDongle}</Text>
            ) : null,
          rowExpandable: (row) => !!row.wifiDongle,
        }}
      />
    );
  };

  const columns: ColumnsType<IMetadataOther> = [
    {
      title: "IMO Number",
      dataIndex: ["subscription", "licence", "vessel", "imoNumber"],
      key: "subscription.licence.vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["subscription", "licence", "vessel", "name"],
      key: "subscription.licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Active Services",
      key: "activeServices",
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ width: 520, marginBottom: 8, display: "block" }}
            placeholder="Select Service"
            allowClear
            value={selectedService}
            options={SERVICE_FILTER_OPTIONS}
            onChange={(value) => setSelectedService(value)}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                const serviceFields = SERVICE_FILTER_OPTIONS.map((o) => o.value);
                const otherFilters = (filters || []).filter(
                  (f) =>
                    !("field" in f && serviceFields.includes(f.field as string)),
                );
                const nextFilters = selectedService
                  ? [
                      ...otherFilters,
                      {
                        field: selectedService,
                        operator: "eq" as const,
                        value: "active",
                      },
                    ]
                  : otherFilters;
                setFilters(nextFilters, "replace");
                confirm();
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              onClick={() => {
                const serviceFields = SERVICE_FILTER_OPTIONS.map((o) => o.value);
                const otherFilters = (filters || []).filter(
                  (f) =>
                    !("field" in f && serviceFields.includes(f.field as string)),
                );
                setSelectedService(undefined);
                setFilters(otherFilters, "replace");
                clearFilters?.();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      render: (_, record) => {
        const active = getActiveServices(record);
        if (active.length === 0) {
          return <Text type="secondary">None</Text>;
        }
        return active.map((svc) => (
          <Tag key={svc.prefix} color={OTHER_PRODUCT_COLORS[svc.product]}>
            {svc.service}
          </Tag>
        ));
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Other Services",
        subTitle: "active other services",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <CreateButton />
            <ExportButton onClick={triggerExport} loading={isLoadingExport} />
          </Space>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 900 }}
        sticky
        expandable={{
          expandedRowRender,
          rowExpandable: hasAnyActive,
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
